import React from 'react';
import { motion } from 'framer-motion';

/**
 * Footer v2.0 - 2026-03-07
 * ✅ 新增：Organization microdata（itemScope/itemProp）
 *    讓 AI 爬蟲從 Footer 確認站點身份，補強 Schema.org 信號
 * ✅ 新增：語義化 <address> 標籤
 * ✅ 新增：網站主要連結（SEO 內鏈強化）
 */
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer
      className="py-16 bg-slate-900/95 dark:bg-black/95 relative overflow-hidden backdrop-blur-sm"
      itemScope
      itemType="https://schema.org/Organization"
    >
      {/* 隱藏的 Organization microdata（供爬蟲讀取） */}
      <meta itemProp="name" content="Omakase TW" />
      <meta itemProp="alternateName" content="お任せ Omakase" />
      <link itemProp="url" href="https://omakase.tw" />
      <meta itemProp="description" content="台灣最完整的 Omakase 日本料理文化指南，涵蓋入門知識、用餐禮儀、季節食材與深度文化解析。" />
      <link itemProp="logo" href="https://omakase.tw/omakase-favicon.svg" />

      <div className="absolute inset-0">
        <motion.div
          className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-purple-500/5 to-transparent"
          animate={{ opacity: [0, 0.3, 0], x: ['-100%', '100%', '-100%'] }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute top-1/4 right-1/4 w-64 h-64 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-full blur-3xl"
          animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.4, 0.2], rotate: [0, 180, 360] }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <div className="container-custom relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          {/* 品牌標識 */}
          <div className="mb-8">
            <motion.h3
              className="text-3xl md:text-4xl font-light text-white mb-2"
              animate={{ textShadow: ['0 0 20px rgba(255,255,255,0.5)', '0 0 30px rgba(255,255,255,0.8)', '0 0 20px rgba(255,255,255,0.5)'] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            >
              お任せ <span className="text-2xl md:text-3xl opacity-80">Omakase</span>
            </motion.h3>
            <p className="text-slate-300 text-lg font-light">交給主廚，信任的藝術</p>
          </div>

          {/* ✅ 新增：主要文章導航連結（強化內鏈 SEO） */}
          <nav aria-label="網站主要連結" className="mb-8">
            <ul className="flex flex-wrap justify-center gap-x-6 gap-y-2">
              {[
                { href: '/beginners-complete-guide-to-omakase', label: '初學者指南' },
                { href: '/complete-omakase-etiquette-guide', label: '用餐禮儀' },
                { href: '/sushi-vs-kaiseki-omakase-comparison', label: '壽司 vs 懷石' },
                { href: '/taiwan-omakase-culture-development', label: '台灣 Omakase 文化' },
                { href: '/articles', label: '所有文章' },
                { href: '/about', label: '關於本站與聯絡方式' },
              ].map(link => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="text-slate-400 hover:text-orange-400 text-sm transition-colors"
                    itemProp="url"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="border-t border-slate-700/50 pt-8">
            <address className="not-italic">
              <p className="text-slate-400 text-sm">
                © {currentYear} Omakase TW
                <span className="block mt-2">致力於傳播日本料理文化的美好與深度</span>
              </p>
            </address>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mt-8"
          >
            <p className="text-slate-500 text-xs italic">
              "在每一次的 Omakase 體驗中，我們不只是在品嚐美食，更是在感受一種生活哲學。"
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="mt-6"
          >
            <p className="text-slate-500 text-sm flex items-center justify-center space-x-2">
              <span>Love by</span>
              <motion.a
                href="https://homy.hk"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gradient bg-gradient-to-r from-rose-400 via-pink-400 to-purple-400 bg-clip-text text-transparent font-medium hover:from-rose-300 hover:via-pink-300 hover:to-purple-300 transition-all relative group"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Homy Tech
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-rose-400/20 to-purple-400/20 rounded opacity-0 group-hover:opacity-100 transition-opacity blur-sm"
                  animate={{ scale: [1, 1.1, 1], opacity: [0, 0.3, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                />
              </motion.a>
            </p>
          </motion.div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;